// src/pages/AdminDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'; // ⭐ Step 1: Import useNavigate
import authService from '../services/authService';
import api from '../services/api'; 

const AdminDashboard = () => {
  const admin = authService.getCurrentUser();
  const navigate = useNavigate(); // ⭐ Step 2: Initialize navigate hook
  
  const [stats, setStats] = useState({
    collegesCount: 0,
    productsCount: 0,
    lowStockCount: 0
  });
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        setErrorMsg(null);
        console.log("Admin Dashboard mounted. Token present:", !!localStorage.getItem('token'));
        
        const [collegesRes, productsRes, lowStockRes] = await Promise.all([
          api.get('/colleges/count'),
          api.get('/products/count'),
          api.get('/admin/products/low-stock?threshold=5')
        ]);
        
        setStats({
          collegesCount: collegesRes.data,
          productsCount: productsRes.data,
          lowStockCount: Array.isArray(lowStockRes.data) ? lowStockRes.data.length : 0
        });
      } catch (error) {
        console.error("CRITICAL: Error fetching dashboard statistics", error);
        setErrorMsg(error.response?.data?.message || "Failed to fetch admin metrics. Verify permissions.");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardStats();
  }, []);

  const handleLogout = () => {
    authService.logout();
    navigate('/'); // Smooth redirection on logout
  };

  return (
    <div style={styles.container}>
      {/* Top Navigation / Header */}
      <header style={styles.header}>
        <div>
          <h1 style={styles.title}>VidyaThreads Control Panel</h1>
          <p style={styles.subtitle}>Welcome back, {admin?.fullName || 'Admin'}</p>
        </div>
        <button onClick={handleLogout} style={styles.logoutBtn}>
          Sign Out
        </button>
      </header>

      <hr style={styles.divider} />

      {/* Error Feedback Banner */}
      {errorMsg && (
        <div style={styles.errorBanner}>
          <strong>Backend Connection Failure:</strong> {errorMsg}
        </div>
      )}

      {/* Grid Layout for Analytics Cards */}
      <section style={styles.grid}>
        <div 
          onClick={() => navigate('/admin/colleges')} // ⭐ Step 3: Switch to navigate()
          style={{ ...styles.card, cursor: 'pointer' }}
        >
          <h3>🏫 Total Colleges</h3>
          <p style={styles.statNumber}>{loading ? '...' : stats.collegesCount}</p>
          <span style={styles.cardLink}>Configure campuses & databases</span>
        </div>

        <div 
          onClick={() => navigate('/admin/products')} // ⭐ Step 3: Switch to navigate()
          style={{ ...styles.card, cursor: 'pointer' }}
        >
          <h3>👕 All Merch Products</h3>
          <p style={styles.statNumber}>{loading ? '...' : stats.productsCount}</p>
          <span style={styles.cardLink}>Edit items, prices & details</span>
        </div>

        <div
          onClick={() => navigate('/admin/orders')}
          style={{ ...styles.card, cursor: 'pointer' }}
        >
          <h3>📦 Orders</h3>
          <p style={styles.statNumber}>—</p>
          <span style={styles.cardLink}>View, update status & supplier info</span>
        </div>

        <div style={styles.cardDanger}>
          <h3>⚠️ Inventory Alerts</h3>
          <p style={styles.statNumberDanger}>{loading ? '...' : stats.lowStockCount}</p>
          <span style={styles.cardLinkDanger}>Product sizes with low stock (under 5 units)</span>
        </div>
      </section>

      {/* Quick-Actions Panel */}
      <section style={styles.managementSection}>
        <h2>Data Management Shortcut Hub</h2>
        <p>Select an architectural table block below to view, add, or alter parameters:</p>
        
        <div style={styles.buttonGroup}>
          <button style={styles.actionBtn} onClick={() => navigate('/admin/colleges')}>
            Manage Colleges Table
          </button>
          <button style={styles.actionBtn} onClick={() => navigate('/admin/products')}>
            Manage Products & Sizes
          </button>
          <button style={styles.actionBtn} onClick={() => navigate('/admin/orders')}>
            Manage Orders
          </button>
        </div>
      </section>
    </div>
  );
};

// Layout Inline Styles remains identical
const styles = {
  container: { padding: '40px', maxWidth: '1200px', margin: '0 auto', fontFamily: 'Segoe UI, Roboto, sans-serif', color: '#333' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  title: { margin: 0, fontSize: '2rem', color: '#111' },
  subtitle: { margin: '5px 0 0 0', color: '#666' },
  logoutBtn: { backgroundColor: '#dc3545', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' },
  divider: { border: '0', height: '1px', background: '#e0e0e0', marginBottom: '30px' },
  errorBanner: { backgroundColor: '#fff5f5', color: '#c53030', border: '1px solid #fed7d7', padding: '15px', borderRadius: '8px', marginBottom: '25px', fontSize: '0.95rem' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginBottom: '40px' },
  card: { background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' },
  cardDanger: { background: '#fff5f5', border: '1px solid #fed7d7', borderRadius: '12px', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' },
  statNumber: { fontSize: '2.5rem', fontWeight: 'bold', margin: '10px 0', color: '#2b6cb0' },
  statNumberDanger: { fontSize: '2.5rem', fontWeight: 'bold', margin: '10px 0', color: '#c53030' },
  cardLink: { fontSize: '0.875rem', color: '#4a5568', textDecoration: 'underline' },
  cardLinkDanger: { fontSize: '0.875rem', color: '#9b2c2c', fontWeight: '500' },
  managementSection: { background: '#f7fafc', borderRadius: '12px', padding: '30px', border: '1px solid #edf2f7' },
  buttonGroup: { display: 'flex', gap: '15px', marginTop: '20px' },
  actionBtn: { backgroundColor: '#2b6cb0', color: 'white', padding: '12px 24px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '1rem', fontWeight: '500', transition: 'background 0.2s' }
};

export default AdminDashboard;